import { PublicClientApplication } from "@azure/msal-browser";
import { getAuth, OAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { initFirebaseApp } from "./firebaseInit";

export type AuthProvider = "firebase" | "azure";

export const AUTH_PROVIDER: AuthProvider = "azure";
  // (process.env.REACT_APP_AUTH_PROVIDER as AuthProvider) || "firebase";

type AzureConfig = {
  tenantId: string;
  clientId: string;
  redirectUri: string;
};

const getAzureConfig = (): AzureConfig => {
  const tenantId = process.env.REACT_APP_AZURE_TENANT_ID || "";
  const clientId = process.env.REACT_APP_AZURE_CLIENT_ID || "";
  const redirectUri =
    process.env.REACT_APP_AZURE_REDIRECT_URI || window.location.origin;

  if (!tenantId || !clientId) {
    throw new Error("Azure auth is enabled but tenantId/clientId are missing.");
  }

  return { tenantId, clientId, redirectUri };
};

let msalInstance: PublicClientApplication | null = null;
let msalInitialized: Promise<void> | null = null;

const getMsalInstance = async () => {
  const { tenantId, clientId, redirectUri } = getAzureConfig();
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii) {
          console.log(`[MSAL ${level}]: ${message}`);
        }
      },
      piiLoggingEnabled: false,
    },
    allowNativeBroker: false,
  },
});

    msalInitialized = msalInstance.initialize();
  }

  if (msalInitialized) {
    await msalInitialized;
  }
  return msalInstance;
};

export const initAuthProvider = async () => {
  if (AUTH_PROVIDER === "azure") {
    await getMsalInstance();
  } else if (AUTH_PROVIDER === "firebase") {
    initFirebaseApp();
  }
};

export const loginWithSso = async (): Promise<string> => {
  if (AUTH_PROVIDER === "azure") {
    const msal = await getMsalInstance();
    const result = await msal.loginPopup({
      scopes: ["openid", "profile", "email"],
    });
    if (!result?.idToken) {
      throw new Error("Azure login did not return an id token.");
    }
    return result.idToken;
  }

  initFirebaseApp();
  const provider = new OAuthProvider("microsoft.com");
  provider.setCustomParameters({
    tenant: process.env.REACT_APP_AZURE_TENANT_ID || "",
  });

  const auth = getAuth();
  await signInWithPopup(auth, provider);

  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new Error("Firebase login did not return an id token.");
  }
  return idToken;
};

export const logoutSso = async (): Promise<void> => {
  localStorage.removeItem("token");
  
  if (AUTH_PROVIDER === "azure") {
    const msal = await getMsalInstance();
    const accounts = msal.getAllAccounts();

    for (const account of accounts) {
      msal.logout({ account, onRedirectNavigate: () => false });
    }
    return;
  }

  const auth = getAuth();
  await signOut(auth);
};

export const acquireTokenSilently = async (): Promise<string | null> => {
  if (AUTH_PROVIDER !== "azure") return null;

  const msal = await getMsalInstance();
  const accounts = msal.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  try {
    const result = await msal.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account: accounts[0],
    });
    return result.idToken;
  } catch (error) {
    console.error("Silent token acquisition failed:", error);
    return null;
  }
};

export const clearAuthCache = async (): Promise<void> => {
  localStorage.removeItem("token");
  
  if (AUTH_PROVIDER === "azure") {
    const storageKeys = Object.keys(localStorage);
    storageKeys.forEach((key) => {
      if (key.startsWith("msal.")) {
        localStorage.removeItem(key);
      }
    });
  }
};